/**
 * InsertMenu — global "new record" hub (traditional "Insert" menu name).
 *
 * The one thing the menubar does that the sidebar can't: create any record
 * from anywhere. Each item navigates to the owning section and fires a
 * create-signal that pops its existing create dialog (see App.js requestCreate
 * + each view's createSignal effect). No new backend — reuses each section's
 * create flow.
 *
 * v1 (lite) covers the top-level create dialogs. NCR / CAPA live one component
 * deeper (Quality Management child tabs) and stay reachable via Tools →
 * Change Control until they're wired in.
 */
import React from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Description,
  Rule,
  Science,
  VerifiedUser,
} from '@mui/icons-material';

const InsertMenu = ({ anchorEl, open, onClose, hasProject, onCreate }) => {
  const wrap = (kind) => () => { onClose(); onCreate?.(kind); };

  const row = (icon, label) => (
    <>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>{label}</ListItemText>
    </>
  );

  const gated = (kind, icon, label) => (
    <Tooltip title={hasProject ? '' : 'Select a product first'} placement="right">
      <span>
        <MenuItem dense disabled={!hasProject} onClick={wrap(kind)}>
          {row(icon, label)}
        </MenuItem>
      </span>
    </Tooltip>
  );

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      <MenuItem dense onClick={wrap('product')}>
        {row(<Add fontSize="small" />, 'New Product…')}
      </MenuItem>
      <Divider />
      {gated('requirement', <Rule fontSize="small" />, 'New Requirement…')}
      {gated('standard', <VerifiedUser fontSize="small" />, 'New Standard…')}
      {gated('session', <Science fontSize="small" />, 'New V&V Session…')}
      {gated('document', <Description fontSize="small" />, 'New Document…')}
    </Menu>
  );
};

export default InsertMenu;
